import React, { useState, useEffect } from 'react';
import { Upload, Trash2, Image, Star, Save, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { mediaApi } from '../../services/api';

const AdminMedia = () => {
  const [media, setMedia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState('');

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        const res = await mediaApi.get();
        setMedia(res.data);
      } catch (error) {
        console.error('Error fetching media:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMedia();
  }, []);

  const addImage = () => {
    if (newImageUrl.trim()) {
      const newGallery = [...(media.gallery || []), { 
        id: `g${Date.now()}`, 
        url: newImageUrl.trim(), 
        isPrimary: false 
      }];
      setMedia({ ...media, gallery: newGallery });
      setNewImageUrl('');
    }
  };

  const removeImage = (id) => {
    setMedia({ ...media, gallery: media.gallery.filter(img => img.id !== id) });
  };

  const setPrimaryImage = (id) => {
    setMedia({ 
      ...media, 
      gallery: media.gallery.map(img => ({ ...img, isPrimary: img.id === id }))
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await mediaApi.update(media);
      alert('Medios guardados correctamente');
    } catch (error) {
      console.error('Error saving media:', error);
      alert('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Media (Poster e Imágenes)</h1>
          <p className="text-gray-500">Gestiona el poster y la galería de imágenes</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#50c878] hover:bg-[#40a0a0]">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Guardar Cambios
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Poster Desktop */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Image className="w-5 h-5 text-[#50c878]" />
              Poster (Escritorio)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="aspect-[2/3] bg-gray-100 rounded-lg overflow-hidden">
              <img 
                src={media?.posterUrl || ''} 
                alt="Poster" 
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = 'https://via.placeholder.com/400x600?text=Poster'; }}
              />
            </div>
            <div>
              <Label htmlFor="posterUrl">URL del Poster (Desktop)</Label>
              <Input
                id="posterUrl"
                value={media?.posterUrl || ''}
                onChange={(e) => setMedia({ ...media, posterUrl: e.target.value })}
                placeholder="https://..."
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Se muestra en la columna izquierda en versión escritorio</p>
            </div>
          </CardContent>
        </Card>

        {/* Poster Mobile */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Image className="w-5 h-5 text-[#50c878]" />
              Poster (Móvil)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="aspect-[16/9] bg-gray-100 rounded-lg overflow-hidden">
              <img 
                src={media?.posterMobileUrl || media?.posterUrl || ''} 
                alt="Poster Mobile" 
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = 'https://via.placeholder.com/800x450?text=Poster+Movil'; }}
              />
            </div>
            <div>
              <Label htmlFor="posterMobileUrl">URL del Poster (Móvil)</Label>
              <Input
                id="posterMobileUrl"
                value={media?.posterMobileUrl || ''}
                onChange={(e) => setMedia({ ...media, posterMobileUrl: e.target.value })}
                placeholder="https://..."
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Se muestra en la cabecera en versión móvil. Si está vacío, se usa el poster de escritorio.</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {/* Hero Image */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Image className="w-5 h-5 text-[#50c878]" />
              Imagen Hero (Fondo)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden max-h-64">
              <img 
                src={media?.heroUrl || ''} 
                alt="Hero" 
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = 'https://via.placeholder.com/1920x1080?text=Hero'; }}
              />
            </div>
            <div>
              <Label htmlFor="heroUrl">URL de la Imagen Hero</Label>
              <Input
                id="heroUrl"
                value={media?.heroUrl || ''}
                onChange={(e) => setMedia({ ...media, heroUrl: e.target.value })}
                placeholder="https://..."
                className="mt-1"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gallery */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Image className="w-5 h-5 text-[#e84c8a]" />
            Galería de Imágenes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
            {media?.gallery?.map((image) => (
              <div key={image.id} className="relative group">
                <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                  <img src={image.url} alt="Gallery" className="w-full h-full object-cover" />
                </div>
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                  <button
                    onClick={() => setPrimaryImage(image.id)}
                    className={`p-2 rounded-full ${image.isPrimary ? 'bg-yellow-500' : 'bg-white/20 hover:bg-white/40'}`}
                    title="Establecer como principal"
                  >
                    <Star className={`w-4 h-4 ${image.isPrimary ? 'text-white fill-white' : 'text-white'}`} />
                  </button>
                  <button
                    onClick={() => removeImage(image.id)}
                    className="p-2 rounded-full bg-red-500 hover:bg-red-600"
                    title="Eliminar"
                  >
                    <Trash2 className="w-4 h-4 text-white" />
                  </button>
                </div>
                {image.isPrimary && (
                  <span className="absolute top-2 left-2 bg-yellow-500 text-white text-xs px-2 py-1 rounded">Principal</span>
                )}
              </div>
            ))}
          </div>

          <div className="flex gap-2">
            <Input
              placeholder="URL de la nueva imagen"
              value={newImageUrl}
              onChange={(e) => setNewImageUrl(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addImage()}
            />
            <Button onClick={addImage} className="bg-[#e84c8a] hover:bg-[#d43d7a]">
              <Upload className="w-4 h-4 mr-2" />
              Añadir
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminMedia;
